import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, CssBaseline, Toolbar, CircularProgress } from '@mui/material';
import Navbar from '../common/Navbar';
import Sidebar from '../common/Sidebar';
import { useAuth } from '../../hooks/useAuth';

const drawerWidth = 260;

const DashboardLayout = () => {
  const { user, loading, isAuthenticated, initialized } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Estados del sidebar con persistencia
  const [open, setOpen] = useState(() => {
    try {
      const savedState = localStorage.getItem('sidebarOpen');
      return savedState !== null ? JSON.parse(savedState) : true;
    } catch {
      return true;
    }
  });
  
  const [mobileOpen, setMobileOpen] = useState(false);

  // Persistir estado del sidebar
  useEffect(() => {
    try {
      localStorage.setItem('sidebarOpen', JSON.stringify(open));
    } catch (error) {
      console.warn('No se pudo guardar estado del sidebar:', error);
    }
  }, [open]);

  // Manejo de redirección - solo cuando ya se inicializó
  useEffect(() => {
    if (initialized && !loading && !isAuthenticated) {
       
      navigate('/login');
    }
  }, [initialized, loading, isAuthenticated, navigate]);

  const handleDrawerToggle = () => setOpen(!open);
  const handleMobileDrawerToggle = () => setMobileOpen(!mobileOpen);

  // Determinar si mostrar sidebar
  // Mostrar si: está autenticado O (está cargando Y hay token Y se inicializó)
  const shouldShowSidebar = isAuthenticated || (loading && !!localStorage.getItem('token') && initialized);

   

  // Loading inicial completo solo si no hay token
  if (loading && !localStorage.getItem('token')) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress />
      </Box>
    );
  }

  // No mostrar nada si no está inicializado aún
  if (!initialized) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Si ya se inicializó pero no está autenticado, no mostrar (redirigirá)
  if (initialized && !isAuthenticated && !loading) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      
      {/* Navbar */}
      <Navbar 
        open={open} 
        drawerWidth={drawerWidth} 
        handleDrawerToggle={handleDrawerToggle}
        handleMobileDrawerToggle={handleMobileDrawerToggle}
      />
      
      {/* Sidebar - mostrar de forma estable */}
      {shouldShowSidebar && (
        <Sidebar 
          open={open} 
          mobileOpen={mobileOpen}
          drawerWidth={drawerWidth} 
          handleDrawerToggle={handleDrawerToggle}
          handleMobileDrawerToggle={handleMobileDrawerToggle}
        />
      )}
      
      {/* Contenido principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { 
            sm: `calc(100% - ${shouldShowSidebar && open ? drawerWidth : 0}px)` 
          },
          marginLeft: { 
            sm: shouldShowSidebar && open ? `${drawerWidth}px` : 0 
          },
          transition: theme => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          bgcolor: 'background.default',
          minHeight: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        
        {/* Mostrar overlay de carga solo durante verificación */}
        {loading && initialized && localStorage.getItem('token') ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={3}
          >
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Outlet />
        )}
      </Box>
    </Box>
  );
};

export default DashboardLayout;